import React, { useState, useEffect } from 'react';
import { Button, Card, Row, Col, Typography, FloatButton } from 'antd';
import { SafetyCertificateOutlined, RocketOutlined, GiftOutlined, TeamOutlined, CustomerServiceOutlined, ShopOutlined, DashboardOutlined } from '@ant-design/icons';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Paragraph } = Typography;

const LandingPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState(0);

  // Auto-rotate features every 4 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 4);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  // If user is already logged in, redirect to appropriate dashboard
  useEffect(() => {
    if (user) {
      const isAdmin = user.isAdmin || user.role === 'admin' || user.role === 'super_admin';
      navigate(isAdmin ? '/admin/dashboard' : '/client/dashboard');
    }
  }, [user, navigate]);

  const features = [
    {
      icon: <ShopOutlined style={{ fontSize: 42, color: '#8B7AAE' }} />,
      title: 'Bulk Procurement',
      description: 'Purchase vouchers in bulk at wholesale prices with instant digital delivery.',
      color: '#8B7AAE'
    },
    {
      icon: <SafetyCertificateOutlined style={{ fontSize: 42, color: '#7B68A6' }} />,
      title: 'Verified Codes',
      description: 'All voucher codes are authenticated and guaranteed to work.',
      color: '#7B68A6'
    },
    {
      icon: <DashboardOutlined style={{ fontSize: 42, color: '#6B5B9D' }} />,
      title: 'Real-time Tracking',
      description: 'Monitor inventory levels and track your orders in real-time.',
      color: '#6B5B9D'
    },
    {
      icon: <TeamOutlined style={{ fontSize: 42, color: '#5A4D93' }} />,
      title: 'Business Tools',
      description: 'Complete dashboard for managing your voucher business operations.',
      color: '#5A4D93'
    }
  ];

  // Real voucher data from API
  const [voucherPreview, setVoucherPreview] = useState([]);

  useEffect(() => {
    fetch('/api/vouchers/preview')
      .then(res => {
        if (!res.ok) throw new Error('Failed to fetch vouchers');
        return res.json();
      })
      .then(data => {
        if (Array.isArray(data.vouchers) && data.vouchers.length > 0) {
          // Get the top 3 vouchers, prioritizing those with better visibility/stock
          const topVouchers = data.vouchers
            .filter(
              v =>
                v.isActive !== false &&
                v.voucherStatus === 'online' &&
                v.stock > 0,
            )
            .sort((a, b) => {
              if (a.featured && !b.featured) return -1;
              if (!a.featured && b.featured) return 1;
              if (a.stock !== b.stock) return b.stock - a.stock;
              return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
            })
            .slice(0, 3);

          if (topVouchers.length > 0) {
            setVoucherPreview(topVouchers.map((v, index) => {
              // Use actual voucher data more intelligently
              const title = v.name || v.brand || 'Premium Voucher';
              const category = v.category || 'digital';
              const brand = v.brand || '';
              
              // Determine icon based on actual category and brand data
              let icon = <GiftOutlined style={{ fontSize: 28, color: '#8B7AAE' }} />;
              
              // Use actual category field
              if (category.toLowerCase().includes('gift') || 
                  category.toLowerCase().includes('shopping') ||
                  brand.toLowerCase().includes('amazon') || 
                  brand.toLowerCase().includes('target') || 
                  brand.toLowerCase().includes('nike')) {
                icon = <ShopOutlined style={{ fontSize: 28, color: '#7B68A6' }} />;
              } else if (category.toLowerCase().includes('entertainment') ||
                        category.toLowerCase().includes('streaming') ||
                        brand.toLowerCase().includes('spotify') || 
                        brand.toLowerCase().includes('netflix')) {
                icon = <CustomerServiceOutlined style={{ fontSize: 28, color: '#6B5B9D' }} />;
              }

              // Build tags from actual voucher properties
              const tags = [];
              if (v.category) tags.push(v.category);
              if (v.type) tags.push(v.type);
              if (v.brand && v.brand !== title) tags.push(v.brand);
              if (v.faceValue) tags.push(`$${v.faceValue}`);
              
              // Fallback tags if none available
              if (tags.length === 0) {
                tags.push('Digital', 'Instant');
              }

              return {
                icon,
                title,
                desc: v.description || v.shortDescription || `${title} available for bulk purchase`,
                tags: tags.slice(0, 2), // Only show first 2 tags
                category: v.category || 'digital',
                stock: v.stock || 0,
                featured: v.featured || false,
                brand: v.brand || '',
                faceValue: v.faceValue || ''
              };
            }));
          }
        } else {
          // Set fallback data for popular voucher types
          setVoucherPreview([
            {
              icon: <ShopOutlined style={{ fontSize: 28, color: '#8B7AAE' }} />,
              title: 'Amazon Gift Cards',
              desc: 'Popular e-commerce vouchers for bulk purchase',
              tags: ['E-commerce', 'Popular'],
              category: 'shopping',
              stock: 999,
              featured: true
            },
            {
              icon: <CustomerServiceOutlined style={{ fontSize: 28, color: '#7B68A6' }} />,
              title: 'Netflix Subscriptions',
              desc: 'Streaming service vouchers in high demand',
              tags: ['Entertainment', 'Digital'],
              category: 'entertainment',
              stock: 500,
              featured: false
            },
            {
              icon: <GiftOutlined style={{ fontSize: 28, color: '#6B5B9D' }} />,
              title: 'Gaming Vouchers',
              desc: 'PlayStation, Xbox, Steam codes available',
              tags: ['Gaming', 'Digital'],
              category: 'gaming',
              stock: 750,
              featured: true
            }
          ]);
        }
      })
      .catch(error => {
        // Set fallback data for popular voucher types
        setVoucherPreview([
          {
            icon: <ShopOutlined style={{ fontSize: 28, color: '#8B7AAE' }} />,
            title: 'Amazon Gift Cards',
            desc: 'Popular e-commerce vouchers for bulk purchase',
            tags: ['E-commerce', 'Popular'],
            category: 'shopping',
            stock: 999,
            featured: true
          },
          {
            icon: <CustomerServiceOutlined style={{ fontSize: 28, color: '#7B68A6' }} />,
            title: 'Netflix Subscriptions',
            desc: 'Streaming service vouchers in high demand',
            tags: ['Entertainment', 'Digital'],
            category: 'entertainment',
            stock: 500,
            featured: false
          },
          {
            icon: <GiftOutlined style={{ fontSize: 28, color: '#6B5B9D' }} />,
            title: 'Gaming Vouchers',
            desc: 'PlayStation, Xbox, Steam codes available',
            tags: ['Gaming', 'Digital'],
            category: 'gaming',
            stock: 750,
            featured: true
          }
        ]);
      });
  }, []);

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(145deg, #FAFBFE 0%, #F4F6FB 30%, #EEF2FA 100%)',
      position: 'relative',
      overflow: 'hidden'
    }}>
      {/* Elegant background elements */}
      <div style={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'radial-gradient(circle at 25% 25%, rgba(139, 122, 174, 0.08) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(123, 104, 166, 0.06) 0%, transparent 50%)',
        zIndex: 1
      }} />

      {/* Header */}
      <motion.div
        initial={{ y: -50, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6 }}
        style={{
          position: 'relative',
          zIndex: 10,
          padding: '20px 48px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'rgba(255, 255, 255, 0.85)',
          backdropFilter: 'blur(20px)',
          borderBottom: '1px solid rgba(139, 122, 174, 0.12)',
          boxShadow: '0 1px 20px rgba(139, 122, 174, 0.08)'
        }}
      >
        {/* Logo */}
        <Link to="/" style={{ display: 'flex', alignItems: 'center', textDecoration: 'none' }}>
          <motion.div
            whileHover={{ scale: 1.02 }}
            style={{ display: 'flex', alignItems: 'center', padding: '8px 0' }}
          >
            <img
              src="/images/vouchain-logo.png"
              alt="Vouchain Logo"
              style={{ height: 48, display: 'block' }}
            />
          </motion.div>
        </Link>
        
        <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
          <Link to="/login">
            <Button 
              type="text" 
              size="large" 
              style={{ 
                color: '#5A4D93', 
                fontWeight: 500,
                height: 44,
                padding: '0 24px',
                borderRadius: 12,
                border: '1px solid rgba(139, 122, 174, 0.15)',
                background: 'rgba(255, 255, 255, 0.6)'
              }}
            >
              Login
            </Button>
          </Link>
          <Link to="/register">
            <Button 
              type="primary" 
              size="large" 
              style={{ 
                background: 'linear-gradient(135deg, #8B7AAE 0%, #7B68A6 100%)',
              borderColor: 'transparent',
                borderRadius: 12,
                fontWeight: 600,
                height: 44,
                padding: '0 32px',
                boxShadow: '0 4px 16px rgba(139, 122, 174, 0.25)'
              }}
            >
              Register
            </Button>
          </Link>
        </div>
      </motion.div>

      {/* Hero Section */}
      <div style={{ 
        position: 'relative', 
        zIndex: 5, 
        padding: '100px 48px 60px',
        textAlign: 'center'
      }}>
        <motion.div
          initial={{ y: 30, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <Title level={1} style={{
            color: '#2D3748',
            fontSize: '3.6rem',
            fontWeight: 800,
            lineHeight: 1.15,
            marginBottom: 24,
            letterSpacing: '-1.2px',
            fontFamily: 'system-ui, -apple-system, sans-serif'
          }}>
            Bulk Voucher Marketplace
            <br />
            <span style={{
              background: 'linear-gradient(135deg, #8B7AAE 0%, #7B68A6 50%, #6B5B9D 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
          }}>
              For Business
            </span>
          </Title>

          {/* Hero subtitle (Paragraph) */}
          <Paragraph style={{
            fontSize: '1.25rem',
            color: '#2D3748', // Dark
            maxWidth: 680,
            margin: '0 auto 48px',
            lineHeight: 1.7,
            fontWeight: 400
          }}>
            Purchase vouchers in bulk at wholesale prices. Access thousands of digital vouchers from top brands 
            with instant delivery and guaranteed authenticity.
          </Paragraph>

            <Link to="/register">
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button 
                type="primary" 
                size="large"
                icon={<RocketOutlined />}
                style={{ 
                  height: 56,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  padding: '0 40px',
                  marginBottom: 60,
                  background: 'linear-gradient(135deg, #8B7AAE 0%, #7B68A6 100%)',
                  borderColor: 'transparent',
                  borderRadius: 16,
                  boxShadow: '0 6px 20px rgba(139, 122, 174, 0.3)'
                }}
              >
                Register to View More
              </Button>
            </motion.div>
            </Link>

          {/* Enhanced Voucher Preview Section */}
          <motion.div 
            id="voucher-preview"
            initial={{ y: 40, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            style={{ margin: '0 auto', maxWidth: 1000 }}
          >
            <Title level={3} style={{ 
              color: '#2D3748', 
              fontWeight: 700, 
              marginBottom: 40, 
              textAlign: 'center',
              fontSize: '1.8rem'
            }}>
              Popular Voucher Preview
          </Title>
            <Row gutter={[24, 24]} justify="center">
            {voucherPreview.map((v, idx) => (
              <Col xs={24} sm={12} md={8} key={idx}>
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ duration: 0.6, delay: 0.6 + (idx * 0.1) }}
                    whileHover={{ y: -6, scale: 1.02 }}
                  >
                <Card
                  hoverable
                      style={{ 
                        borderRadius: 20, 
                        height: 280,
                        background: 'rgba(255, 255, 255, 0.85)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(139, 122, 174, 0.15)',
                        boxShadow: '0 8px 32px rgba(139, 122, 174, 0.12)',
                        transition: 'all 0.3s ease',
                        overflow: 'hidden',
                        display: 'flex',
                        flexDirection: 'column'
                      }}
                      bodyStyle={{ 
                        padding: 24, 
                        height: '100%',
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'space-between'
                      }}
                    >
                      <div style={{ textAlign: 'center', position: 'relative', flex: 1 }}>
                        {v.featured && (
                          <div style={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            background: 'linear-gradient(135deg, #FEB2B2, #F56565)',
                            color: 'white',
                            borderRadius: 12,
                            padding: '4px 8px',
                            fontSize: 10,
                            fontWeight: 700,
                            boxShadow: '0 4px 12px rgba(245, 101, 101, 0.3)',
                            letterSpacing: '0.5px'
                          }}>
                            HOT
                          </div>
                        )}
                        
                        <div style={{
                          background: 'linear-gradient(135deg, rgba(139, 122, 174, 0.1), rgba(123, 104, 166, 0.08))',
                          borderRadius: 16,
                          padding: 20,
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          marginBottom: 16,
                          width: 64,
                          height: 64,
                          border: '1px solid rgba(139, 122, 174, 0.1)',
                          boxShadow: '0 4px 16px rgba(139, 122, 174, 0.1)'
                        }}>
                    {v.icon}
                        </div>
                        
                        <div style={{ 
                          fontWeight: 700, 
                          fontSize: 16, 
                          color: '#2D3748', 
                          marginBottom: 8, 
                          lineHeight: 1.3,
                          minHeight: 40,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {v.title}
                        </div>
                        
                        <div style={{ 
                          color: '#718096', 
                          fontSize: 13, 
                          marginBottom: 16, 
                          lineHeight: 1.4,
                          minHeight: 36,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}>
                          {v.desc}
                  </div>
                        
                        <div style={{ 
                          display: 'flex', 
                          gap: 6, 
                          justifyContent: 'center', 
                          flexWrap: 'wrap', 
                          marginBottom: 12,
                          minHeight: 28
                        }}>
                    {v.tags.map((tag, i) => (
                            <span key={i} style={{ 
                              background: 'linear-gradient(135deg, rgba(139, 122, 174, 0.15), rgba(123, 104, 166, 0.1))', 
                              color: '#5A4D93', 
                              borderRadius: 12, 
                              padding: '4px 10px', 
                              fontSize: 11, 
                              fontWeight: 600,
                              border: '1px solid rgba(139, 122, 174, 0.15)'
                            }}>
                              {tag}
                            </span>
                          ))}
                        </div>
                      </div>
                      
                      <div style={{ textAlign: 'center' }}>
                        {v.stock > 0 && (
                          <div style={{
                            fontSize: 12,
                            color: '#38A169',
                            fontWeight: 600,
                            background: 'rgba(56, 161, 105, 0.1)',
                            padding: '6px 12px',
                            borderRadius: 12,
                            display: 'inline-block'
                          }}>
                            ✓ In Stock ({v.stock > 999 ? '999+' : v.stock})
                          </div>
                        )}
                  </div>
                </Card>
                  </motion.div>
              </Col>
            ))}
          </Row>
          </motion.div>
        </motion.div>
      </div>

      {/* Enhanced Features Section */}
      <div id="features" style={{ 
        padding: '60px 48px',
        background: 'rgba(255, 255, 255, 0.6)',
        backdropFilter: 'blur(10px)',
        position: 'relative'
      }}>
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
          <motion.div
            initial={{ y: 40, opacity: 0 }}
            whileInView={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            style={{ textAlign: 'center', marginBottom: 50 }}
          >
            <Title level={2} style={{ 
              fontSize: '2.4rem', 
              marginBottom: 20, 
              color: '#2D3748',
              fontWeight: 700,
              fontFamily: 'system-ui, -apple-system, sans-serif'
            }}>
              Why Businesses Choose Us
            </Title>
            <Paragraph style={{ 
              fontSize: '1.2rem', 
              color: '#4A5568', 
              maxWidth: 600, 
              margin: '0 auto'
            }}>
              Built for businesses who need reliable inventory and competitive wholesale pricing.
            </Paragraph>
          </motion.div>

          <Row gutter={[24, 24]} align="stretch">
            {features.map((feature, index) => (
              <Col xs={24} sm={12} md={6} key={index}>
                <motion.div
                  initial={{ y: 40, opacity: 0 }}
                  whileInView={{ y: 0, opacity: 1 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  whileHover={{ y: -6, scale: 1.02 }}
                  style={{ height: '100%' }}
                >
                  <Card
                    style={{
                      height: '100%',
                      border: activeFeature === index ? `2px solid ${feature.color}` : '1px solid rgba(139, 122, 174, 0.15)',
                      borderRadius: 20,
                      background: activeFeature === index 
                        ? 'rgba(255, 255, 255, 0.95)' 
                        : 'rgba(255, 255, 255, 0.8)',
                      backdropFilter: 'blur(20px)',
                      boxShadow: activeFeature === index 
                        ? `0 8px 32px ${feature.color}20` 
                        : '0 4px 16px rgba(139, 122, 174, 0.12)',
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      display: 'flex',
                      flexDirection: 'column'
                    }}
                    hoverable
                    onMouseEnter={() => setActiveFeature(index)}
                    bodyStyle={{ 
                      padding: 24, 
                      textAlign: 'center',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between'
                    }}
                  >
                    <div>
                      <div style={{
                        background: `linear-gradient(135deg, ${feature.color}15, ${feature.color}10)`,
                        borderRadius: 16,
                        padding: 16,
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginBottom: 16,
                        width: 64,
                        height: 64,
                        border: `1px solid ${feature.color}20`,
                        boxShadow: `0 4px 12px ${feature.color}15`
                      }}>
                      {feature.icon}
                    </div>
                      <Title level={4} style={{ marginBottom: 10, fontSize: '1.1rem', fontWeight: 600, color: '#2D3748' }}>
                      {feature.title}
                    </Title>
                    </div>
                    <Paragraph style={{ color: '#718096', marginBottom: 0, lineHeight: 1.5, fontSize: '0.9rem' }}>
                      {feature.description}
                    </Paragraph>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </div>
      </div>

      {/* Footer - simple and direct */}
      <footer
        style={{
          padding: '48px 24px 32px',
          background: '#F4F6FB',
          borderTop: '1px solid rgba(139, 122, 174, 0.1)',
        }}
      >
        <div
                      style={{
            maxWidth: 1200,
            margin: '0 auto',
                        display: 'flex',
            flexDirection: 'column',
                        alignItems: 'center',
            gap: 32
          }}
        >
          {/* Logo area */}
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <img
              src="/images/vouchain-logo.png"
              alt="Vouchain Logo"
              style={{ height: 40 }}
            />
          </div>

          {/* Description */}
          <div style={{ textAlign: 'center', color: '#4A5568', fontSize: '16px' }}>
              B2B voucher marketplace for businesses and distributors.
          </div>

          {/* Link area - ensure clickable */}
          <div style={{ 
            display: 'flex', 
            gap: '40px', 
            flexWrap: 'wrap',
            justifyContent: 'center',
            pointerEvents: 'auto',
            position: 'relative',
            zIndex: 1000
          }}>
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = '/privacy-policy';
              }}
              style={{
                background: 'rgba(139, 122, 174, 0.05)',
                border: '1px solid rgba(139, 122, 174, 0.2)',
                color: '#2D3748',
                fontSize: '16px',
                fontWeight: 500,
                cursor: 'pointer',
                padding: '12px 20px',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                outline: 'none',
                pointerEvents: 'auto',
                position: 'relative',
                zIndex: 1001
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(139, 122, 174, 0.15)';
                e.currentTarget.style.color = '#5A4D93';
                e.currentTarget.style.borderColor = 'rgba(139, 122, 174, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(139, 122, 174, 0.05)';
                e.currentTarget.style.color = '#2D3748';
                e.currentTarget.style.borderColor = 'rgba(139, 122, 174, 0.2)';
              }}
            >
              Privacy Policy
            </button>

            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = '/terms-of-service';
              }}
              style={{
                background: 'rgba(139, 122, 174, 0.05)',
                border: '1px solid rgba(139, 122, 174, 0.2)',
                color: '#2D3748',
                fontSize: '16px',
                fontWeight: 500,
                cursor: 'pointer',
                padding: '12px 20px',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                outline: 'none',
                pointerEvents: 'auto',
                position: 'relative',
                zIndex: 1001
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(139, 122, 174, 0.15)';
                e.currentTarget.style.color = '#5A4D93';
                e.currentTarget.style.borderColor = 'rgba(139, 122, 174, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(139, 122, 174, 0.05)';
                e.currentTarget.style.color = '#2D3748';
                e.currentTarget.style.borderColor = 'rgba(139, 122, 174, 0.2)';
              }}
            >
              Terms of Service
            </button>

            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = '/security';
              }}
              style={{
                background: 'rgba(139, 122, 174, 0.05)',
                border: '1px solid rgba(139, 122, 174, 0.2)',
                color: '#2D3748',
                fontSize: '16px',
                fontWeight: 500,
                cursor: 'pointer',
                padding: '12px 20px',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                outline: 'none',
                pointerEvents: 'auto',
                position: 'relative',
                zIndex: 1001
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(139, 122, 174, 0.15)';
                e.currentTarget.style.color = '#5A4D93';
                e.currentTarget.style.borderColor = 'rgba(139, 122, 174, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(139, 122, 174, 0.05)';
                e.currentTarget.style.color = '#2D3748';
                e.currentTarget.style.borderColor = 'rgba(139, 122, 174, 0.2)';
              }}
            >
              Security
            </button>

            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                window.location.href = '/support';
              }}
              style={{
                background: 'rgba(139, 122, 174, 0.05)',
                border: '1px solid rgba(139, 122, 174, 0.2)',
                color: '#2D3748',
                fontSize: '16px',
                fontWeight: 500,
                cursor: 'pointer',
                padding: '12px 20px',
                borderRadius: '8px',
                transition: 'all 0.2s ease',
                outline: 'none',
                pointerEvents: 'auto',
                position: 'relative',
                zIndex: 1001
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(139, 122, 174, 0.15)';
                e.currentTarget.style.color = '#5A4D93';
                e.currentTarget.style.borderColor = 'rgba(139, 122, 174, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = 'rgba(139, 122, 174, 0.05)';
                e.currentTarget.style.color = '#2D3748';
                e.currentTarget.style.borderColor = 'rgba(139, 122, 174, 0.2)';
              }}
            >
              Support
            </button>
          </div>

          {/* Copyright */}
          <div style={{ 
            textAlign: 'center', 
            color: '#718096', 
            fontSize: '14px',
            borderTop: '1px solid rgba(139, 122, 174, 0.1)',
            paddingTop: 24,
            width: '100%'
          }}>
              © 2024 Vouchain. All rights reserved.
          </div>
        </div>
      </footer>

      {/* Enhanced Floating Action Button */}
      <FloatButton
        icon={<RocketOutlined />}
        tooltip="Register"
        onClick={() => navigate('/register')}
                  style={{
          right: 24, 
          bottom: 24,
          background: 'linear-gradient(135deg, #8B7AAE 0%, #7B68A6 100%)',
          borderColor: 'transparent',
          boxShadow: '0 6px 20px rgba(139, 122, 174, 0.3)'
        }}
      />

      {/* CSS Animations */}
      <style>{`
        .ant-card:hover {
          transform: translateY(-6px) scale(1.02);
          transition: all 0.3s ease;
        }
        .ant-btn:hover {
          transform: translateY(-2px);
          transition: all 0.2s ease;
        }
      `}</style>
    </div>
  );
};

export default LandingPage;
